import { useEffect, useRef, useState } from "react";
import { api } from "../../services/apiService";

const CONNECT_TIMEOUT_MS = 15000;
const STALL_TIMEOUT_MS = 40000;
const POLL_INTERVAL_MS = 5000;
const SSE_RETRY_INTERVAL_MS = 2 * 60 * 1000;

export default function useTrainDashboardMonitor(trainId) {

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("sse");

    const modeRef = useRef("sse");
    const lastActivityRef = useRef(Date.now());
    const cancelledRef = useRef(false);
    const loadingRef = useRef(false);

    const setModeSafe = (newMode) => {
        modeRef.current = newMode;
        setMode(newMode);
        console.log(`%c[MONITOR] modo atual: ${newMode.toUpperCase()}`, "font-weight:bold;color:#2b7;");
    };

    useEffect(() => {

        if (!trainId) return;

        let eventSource;
        let watchdog;
        let pollingTimeoutId;
        let pollingActive = false;
        let sseRetryInterval;
        let probeEventSource;
        cancelledRef.current = false;
        modeRef.current = "sse";

        //
        // Polling: agora é 1 única chamada ao snapshot já pronto no cache do backend
        //
        const fetchSnapshot = async () => {

            if (loadingRef.current) return;
            loadingRef.current = true;

            try {
                const snapshot = await api.trainDashboard.snapshot(trainId);

                if (cancelledRef.current) return;

                setData(snapshot);
                setLoading(false);

            } catch (err) {
                console.error("[Polling] erro ao buscar snapshot", err);
            } finally {
                loadingRef.current = false;
            }

        };

        const pollingLoop = async () => {
            pollingActive = true;
            while (pollingActive && !cancelledRef.current) {
                await fetchSnapshot();
                if (!pollingActive || cancelledRef.current) break;
                await new Promise(resolve => {
                    pollingTimeoutId = setTimeout(resolve, POLL_INTERVAL_MS);
                });
            }
        };

        const stopPolling = () => {
            pollingActive = false;
            if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
        };

        const startSseProbe = () => {

            clearInterval(sseRetryInterval);

            sseRetryInterval = setInterval(async () => {

                if (cancelledRef.current || modeRef.current === "sse") return;

                const url = await api.trainDashboard.streamUrl(trainId);
                probeEventSource = new EventSource(url, { withCredentials: true });

                const probeTimeout = setTimeout(() => {
                    console.log("[MONITOR] sonda SSE falhou, continua no polling");
                    probeEventSource?.close();
                }, CONNECT_TIMEOUT_MS);

                const sonda = probeEventSource;
                let promovida = false;

                const promoteToSse = () => {
                    if (promovida) return;
                    promovida = true;

                    sonda.removeEventListener("ping", promoteToSse);
                    sonda.onmessage = null;

                    clearTimeout(probeTimeout);

                    stopPolling();
                    clearInterval(sseRetryInterval);

                    eventSource = sonda;
                    attachMainSseHandlers();
                    setModeSafe("sse");
                };

                probeEventSource.onmessage = promoteToSse;
                probeEventSource.addEventListener("ping", promoteToSse);

                probeEventSource.onerror = () => {
                    clearTimeout(probeTimeout);
                    probeEventSource?.close();
                };

            }, SSE_RETRY_INTERVAL_MS);

        };

        const startPolling = () => {

            if (cancelledRef.current) return;
            
            if (pollingActive) return;

            setModeSafe("polling");

            pollingLoop();
            startSseProbe();

        };

        // Handler nomeado: addEventListener ignora registro duplicado da mesma referência.
        const marcarAtividade = () => {
            lastActivityRef.current = Date.now();
        };

        const attachMainSseHandlers = () => {

            lastActivityRef.current = Date.now();

            eventSource.onopen = () => {
                lastActivityRef.current = Date.now();
                console.log("[MONITOR] SSE conectado (onopen)");
            };

            eventSource.onmessage = (event) => {
                lastActivityRef.current = Date.now();
                try {
                    const snapshot = JSON.parse(event.data);
                    setData(snapshot);
                    setLoading(false);
                } catch (err) {
                    console.error("[SSE] erro ao converter snapshot");
                }
            };

            eventSource.addEventListener("ping", marcarAtividade);

            eventSource.onerror = () => {
                console.log("[MONITOR] SSE error/onerror — trocando para polling");
                eventSource.close();
                clearInterval(watchdog);
                startPolling();
            };

            clearInterval(watchdog);
            watchdog = setInterval(() => {
                const idle = Date.now() - lastActivityRef.current;
                const limite = eventSource.readyState === 1 ? STALL_TIMEOUT_MS : CONNECT_TIMEOUT_MS;

                if (idle > limite) {
                    console.log(`[MONITOR] SSE travado (${idle}ms sem atividade, readyState=${eventSource.readyState}) — trocando para polling`);
                    eventSource.close();
                    clearInterval(watchdog);
                    startPolling();
                }
            }, 3000);

        };

        const tryEventSource = async () => {

            if (cancelledRef.current) return;

            const url = await api.trainDashboard.streamUrl(trainId);

            eventSource = new EventSource(url, { withCredentials: true });
            attachMainSseHandlers();

        };

        tryEventSource();

        return () => {
            cancelledRef.current = true;
            stopPolling();
            clearInterval(watchdog);
            clearInterval(sseRetryInterval);
            eventSource?.close();
            probeEventSource?.close();
        };

    }, [trainId]);

    return { data, loading, mode };
}